import { memo, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Div, Image, Loader, Table, Text, useMediaQuery, useTheme } from "react-better-html";

import { type PropertyFull } from "../../types/property";
import { type ListRenderType } from "../../types/app";

import { useI18n } from "../../utils/i18n";
import { formatPrice, getPropertyTitle } from "../../utils/functions";

import PropertyCard from "./PropertyCard";

type PropertiesListProps = {
   properties?: PropertyFull[];
   /** @default "grid" */
   renderType?: ListRenderType;
   flatList?: boolean;
   noLoading?: boolean;
   noEmptyState?: boolean;
   getSinglePropertyUrl?: (property: PropertyFull) => string;
};

function PropertiesList({
   properties,
   renderType = "grid",
   noLoading,
   noEmptyState,
   getSinglePropertyUrl,
}: PropertiesListProps) {
   const theme = useTheme();
   const { t } = useI18n();
   const mediaQuery = useMediaQuery();
   const navigate = useNavigate();

   const getPropertyUrl = useCallback(
      (property: PropertyFull): string => getSinglePropertyUrl?.(property) ?? `/properties/${property.property.id}`,
      [getSinglePropertyUrl],
   );
   const onClickProperty = useCallback(
      (property: PropertyFull, event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
         const singlePropertyUrl = getPropertyUrl(property);

         if (event.metaKey || event.ctrlKey) window.open(singlePropertyUrl, "_blank");
         else navigate(singlePropertyUrl);
      },
      [getPropertyUrl],
   );
   const onClickTableRow = useCallback(
      (item: PropertyFull) => {
         navigate(getPropertyUrl(item));
      },
      [getPropertyUrl],
   );

   const filteredProperties = useMemo(
      () => properties?.filter((property) => !property.metadata.archivedAt),
      [properties],
   );

   return filteredProperties ? (
      filteredProperties.length ? (
         renderType === "grid" ? (
            <Div.grid
               gridTemplateColumns={
                  mediaQuery.size600
                     ? "1fr 1fr"
                     : `repeat(auto-fill, minmax(${mediaQuery.size1000 ? 240 : 280}px, 1fr))`
               }
               gap={theme.styles.gap}
            >
               {filteredProperties?.map((property) => (
                  <PropertyCard
                     property={property}
                     withActionsMenu
                     onClick={onClickProperty}
                     key={property.property.id}
                  />
               ))}
            </Div.grid>
         ) : (
            <Table
               columns={[
                  {
                     type: "element",
                     label: t((l) => l.common.property),
                     minWidth: 350,
                     render: (item) => (
                        <Div.row alignItems="center" gap={theme.styles.gap}>
                           <Image
                              src={item.property.mainImage?.url}
                              width={64}
                              height={64}
                              borderRadius={theme.styles.borderRadius}
                              objectFit="cover"
                              flexShrink={0}
                           />

                           <Text flex={1} fontWeight={700}>
                              {getPropertyTitle(item.property)}
                           </Text>
                        </Div.row>
                     ),
                  },
                  {
                     type: "text",
                     label: t((l) => l.common.price),
                     width: 120,
                     minWidth: 120,
                     format: (item) =>
                        formatPrice(item.property.lowestActivePrice ?? 0, item.property.lowestActiveCurrency),
                  },
                  {
                     type: "text",
                     label: t((l) => l.common.area),
                     format: (item) => `${item.property.areaSqm ?? 0}m²`,
                  },
                  {
                     type: "text",
                     align: "center",
                     label: t((l) => l.common.beds),
                     format: (item) => (item.property.bedrooms ?? 0).toString(),
                  },
                  {
                     type: "text",
                     label: t((l) => l.common.status),
                     format: (item) =>
                        t((l) =>
                           item.property.availability
                              ? l.enums.propertyAvailability[item.property.availability]
                              : l.enums.propertyAvailability.UNKNOWN,
                        ),
                  },
               ]}
               data={filteredProperties}
               onClickRow={onClickTableRow}
            />
         )
      ) : !noEmptyState ? (
         <Text.unknown color={theme.colors.textSecondary + "a0"}>
            {t((l) => l.pages.properties.emptyStateMessage)}
         </Text.unknown>
      ) : undefined
   ) : !noLoading ? (
      <Loader.box />
   ) : undefined;
}

export default memo(PropertiesList);
